/**
 * PDF Generator Utilities
 * Functions to generate and download PDFs using @react-pdf/renderer
 */

import { pdf } from '@react-pdf/renderer';
import { EntityReportPDF, EntityReportData } from './EntityReportPDF';
import { SentinelChatPDF, SentinelChatData } from './SentinelChatPDF';

/**
 * Capture an HTML element as a base64 image
 * @param elementId - The ID of the element to capture
 * @param minHeight - Minimum height in pixels to capture (skip if smaller, e.g., collapsed elements)
 */
export async function captureElementAsImage(elementId: string, minHeight?: number): Promise<string | null> {
  const element = document.getElementById(elementId);
  if (!element) {
    console.warn(`[PDF] Element not found: ${elementId}`);
    return null;
  }

  // Check if element meets minimum height requirement (for collapsible elements)
  if (minHeight && element.offsetHeight < minHeight) {
    console.warn(`[PDF] Element ${elementId} is too small (${element.offsetHeight}px < ${minHeight}px), skipping capture`);
    return null;
  }

  try {
    // Try to find and capture WebGL canvas first (for maps)
    const mapCanvas = element.querySelector('.mapboxgl-canvas, .maplibregl-canvas') as HTMLCanvasElement;
    if (mapCanvas) {
      try {
        return mapCanvas.toDataURL('image/png');
      } catch (e) {
        console.warn('[PDF] Could not capture map canvas directly:', e);
      }
    }

    // Fallback to html2canvas for regular elements
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(element, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error(`[PDF] Error capturing element ${elementId}:`, error);
    return null;
  }
}

/**
 * Generate and download an Entity Report PDF
 */
export async function generateEntityReportPDF(
  data: EntityReportData,
  filename?: string
): Promise<void> {
  try {
    // Capture images if element IDs are provided
    const images: EntityReportData['images'] = {};
    
    // Try to capture full dashboard first
    const dashboardImage = await captureElementAsImage('link-analysis-dashboard');
    if (dashboardImage) images.dashboard = dashboardImage;
    
    // Try to capture graph
    const graphImage = await captureElementAsImage('network-graph-container');
    if (graphImage) images.graph = graphImage;
    
    // Try to capture map (if visible)
    const mapImage = await captureElementAsImage('network-map-container');
    if (mapImage) images.map = mapImage;
    
    // Try to capture charts (if visible)
    const chartsImage = await captureElementAsImage('network-charts-panel');
    if (chartsImage) images.charts = chartsImage;
    
    // Try to capture right panel (if visible)
    const rightPanelImage = await captureElementAsImage('network-right-panel');
    if (rightPanelImage) images.rightPanel = rightPanelImage;

    // Create the document with captured images
    const dataWithImages: EntityReportData = {
      ...data,
      images: {
        ...data.images,
        ...images,
      },
    };

    // Generate PDF using JSX
    const blob = await pdf(<EntityReportPDF data={dataWithImages} />).toBlob();
    
    // Download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `Entity_Report_${data.entityLabel}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('[PDF] Entity report generated successfully');
  } catch (error) {
    console.error('[PDF] Error generating entity report:', error);
    throw error;
  }
}

/**
 * Generate and download a Sentinel Chat PDF
 */
export async function generateSentinelChatPDF(
  data: SentinelChatData,
  filename?: string,
  captureImages?: {
    mapElementId?: string;
    graphElementId?: string;
    timelineElementId?: string;
  }
): Promise<void> {
  try {
    // Capture images if element IDs are provided
    const images: SentinelChatData['images'] = {};
    
    if (captureImages?.mapElementId) {
      const mapImage = await captureElementAsImage(captureImages.mapElementId);
      if (mapImage) images.map = mapImage;
    }
    
    if (captureImages?.graphElementId) {
      const graphImage = await captureElementAsImage(captureImages.graphElementId);
      if (graphImage) images.graph = graphImage;
    }
    
    if (captureImages?.timelineElementId) {
      // Dispatch event to expand the timeline widget before capture
      console.log('[PDF] Requesting timeline expansion for capture');
      window.dispatchEvent(new CustomEvent('timeline-expand-for-capture'));
      
      // Wait for expansion animation to complete (MUI Collapse default is 300ms)
      await new Promise(resolve => setTimeout(resolve, 400));
      
      // Use minHeight of 100 to skip if still collapsed (just header bar is ~50px)
      const timelineImage = await captureElementAsImage(captureImages.timelineElementId, 100);
      if (timelineImage) images.timeline = timelineImage;
    }

    // Create the document with captured images
    const dataWithImages: SentinelChatData = {
      ...data,
      images: {
        ...data.images,
        ...images,
      },
    };

    // Generate PDF using JSX
    const blob = await pdf(<SentinelChatPDF data={dataWithImages} />).toBlob();
    
    // Download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `Sentinel_Chat_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('[PDF] Sentinel chat exported successfully');
  } catch (error) {
    console.error('[PDF] Error generating sentinel chat PDF:', error);
    throw error;
  }
}

/**
 * Helper to format a safe filename
 */
export function formatFilename(base: string, extension: string = 'pdf'): string {
  const safe = base.replace(/[^a-zA-Z0-9-_]/g, '_');
  const date = new Date().toISOString().split('T')[0];
  return `${safe}_${date}.${extension}`;
}


